import { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { KiteTag } from '@kite/react-kite';

import './SideBySideComparison.scss';

interface SideBySideComparisonProps {
  sourcePdfBase64?: string;
  targetPdfBase64?: string;
  sourcePageNumber: number;
  targetPageNumber: number;
  scale: number;
  sourceVersion: string;
  targetVersion: string;
  availableVersions: string[];
  onSourceVersionChange: (version: string) => void;
  onTargetVersionChange: (version: string) => void;
  onSourceLoadSuccess: (data: { numPages: number }) => void;
  onTargetLoadSuccess: (data: { numPages: number }) => void;
}

interface DifferenceRegion {
  x: number;
  y: number;
  width: number;
  height: number;
  isTextChange?: boolean; // Flag to identify text-based changes
}

const SideBySideComparison = ({
  sourcePdfBase64,
  targetPdfBase64,
  sourcePageNumber,
  targetPageNumber,
  scale,
  sourceVersion,
  targetVersion,
  availableVersions,
  onSourceVersionChange,
  onTargetVersionChange,
  onSourceLoadSuccess,
  onTargetLoadSuccess,
}: SideBySideComparisonProps) => {
  const sourceCanvasRef = useRef<HTMLDivElement>(null);
  const targetCanvasRef = useRef<HTMLDivElement>(null);
  const [differenceRegions, setDifferenceRegions] = useState<
    DifferenceRegion[]
  >([]);
  const [isComparing, setIsComparing] = useState(false);

  useEffect(() => {
    // Reset differences when page changes
    setDifferenceRegions([]);
  }, [sourcePageNumber, targetPageNumber]);

  const mergeOverlappingRegions = (
    regions: DifferenceRegion[]
  ): DifferenceRegion[] => {
    if (regions.length === 0) return regions;

    const merged: DifferenceRegion[] = [];
    const used = new Set<number>();

    for (let i = 0; i < regions.length; i++) {
      if (used.has(i)) continue;

      let current = { ...regions[i] };
      let mergedAny = true;

      while (mergedAny) {
        mergedAny = false;
        for (let j = 0; j < regions.length; j++) {
          if (i === j || used.has(j)) continue;

          const other = regions[j];
          // Only merge regions of the same type (both text or both non-text)
          if (current.isTextChange !== other.isTextChange) continue;

          // Check if regions overlap or are close to each other
          const proximity = 20; // Merge if within 20px
          if (
            current.x < other.x + other.width + proximity &&
            current.x + current.width + proximity > other.x &&
            current.y < other.y + other.height + proximity &&
            current.y + current.height + proximity > other.y
          ) {
            // Merge regions
            const newMinX = Math.min(current.x, other.x);
            const newMinY = Math.min(current.y, other.y);
            const newMaxX = Math.max(
              current.x + current.width,
              other.x + other.width
            );
            const newMaxY = Math.max(
              current.y + current.height,
              other.y + other.height
            );

            current = {
              x: newMinX,
              y: newMinY,
              width: newMaxX - newMinX,
              height: newMaxY - newMinY,
              isTextChange: current.isTextChange, // Preserve the type
            };

            used.add(j);
            mergedAny = true;
          }
        }
      }

      merged.push(current);
      used.add(i);
    }

    return merged;
  };

  const findDifferenceRegions = (
    diffData: ImageData,
    width: number,
    height: number
  ): DifferenceRegion[] => {
    const regions: DifferenceRegion[] = [];
    const visited = new Set<number>();
    const blockSize = 5; // Smaller block size for better precision

    for (let y = 0; y < height; y += blockSize) {
      for (let x = 0; x < width; x += blockSize) {
        const pixelIndex = (y * width + x) * 4;

        if (visited.has(pixelIndex) || diffData.data[pixelIndex + 3] === 0) {
          continue;
        }

        // Found a difference, create a region
        let minX = x;
        let maxX = x;
        let minY = y;
        let maxY = y;

        // Expand the region to capture connected differences (flood fill approach)
        const scanRadius = blockSize * 5; // Larger scan radius
        for (let dy = 0; dy < scanRadius && y + dy < height; dy++) {
          for (let dx = 0; dx < scanRadius && x + dx < width; dx++) {
            const idx = ((y + dy) * width + (x + dx)) * 4;
            if (diffData.data[idx + 3] > 0) {
              minX = Math.min(minX, x + dx);
              maxX = Math.max(maxX, x + dx);
              minY = Math.min(minY, y + dy);
              maxY = Math.max(maxY, y + dy);
              visited.add(idx);
            }
          }
        }

        // Add padding to make the region more visible
        const padding = 8;
        const regionWidth = maxX - minX + blockSize + padding * 2;
        const regionHeight = maxY - minY + blockSize + padding * 2;

        // Classify regions: smaller, horizontally-oriented regions are likely text
        const aspectRatio = regionWidth / regionHeight;
        const area = regionWidth * regionHeight;
        const isTextChange =
          area < 15000 && regionHeight < 100 && aspectRatio > 1.5;

        regions.push({
          x: Math.max(0, minX - padding),
          y: Math.max(0, minY - padding),
          width: regionWidth,
          height: regionHeight,
          isTextChange,
        });
      }
    }

    // Merge overlapping or nearby regions
    return mergeOverlappingRegions(regions);
  };

  const drawDifferenceOverlay = (
    canvas: HTMLCanvasElement,
    regions: DifferenceRegion[]
  ) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Draw highlighting similar to the screenshot style
    regions.forEach((region) => {
      // Add padding to the region for better visibility
      const padding = 4;
      const x = Math.max(0, region.x - padding);
      const y = Math.max(0, region.y - padding);
      const width = region.width + padding * 2;
      const height = region.height + padding * 2;

      // Only draw fill and borders for non-text changes (images/diagrams)
      if (!region.isTextChange) {
        // Semi-transparent red fill
        ctx.fillStyle = 'rgba(255, 50, 50, 0.25)';
        ctx.fillRect(x, y, width, height);

        // Add a dotted/dashed border pattern
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.85)';
        ctx.lineWidth = 3;
        ctx.setLineDash([8, 4]); // Dashed line pattern
        ctx.strokeRect(x, y, width, height);

        // Add inner solid border for emphasis
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
        ctx.lineWidth = 1;
        ctx.setLineDash([]); // Solid line
        ctx.strokeRect(x + 2, y + 2, width - 4, height - 4);

        // Add subtle shadow effect for depth
        ctx.shadowColor = 'rgba(255, 0, 0, 0.3)';
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.strokeRect(x, y, width, height);

        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      }
    });

    // Reset line dash
    ctx.setLineDash([]);
  };

  const comparePdfPages = () => {
    if (!sourceCanvasRef.current || !targetCanvasRef.current) return;

    setIsComparing(true);

    // Small delay to ensure PDFs are fully rendered
    setTimeout(() => {
      const sourceCanvas = sourceCanvasRef.current;
      const targetCanvas = targetCanvasRef.current;

      if (!sourceCanvas || !targetCanvas) {
        setIsComparing(false);
        return;
      }

      // Get the actual rendered PDF canvases from react-pdf
      const sourcePdfCanvas = sourceCanvas.querySelector('canvas');
      const targetPdfCanvas = targetCanvas.querySelector('canvas');

      if (!sourcePdfCanvas || !targetPdfCanvas) {
        setIsComparing(false);
        return;
      }

      const sourceCtx = sourcePdfCanvas.getContext('2d');
      const targetCtx = targetPdfCanvas.getContext('2d');

      if (!sourceCtx || !targetCtx) {
        setIsComparing(false);
        return;
      }

      const width = Math.min(sourcePdfCanvas.width, targetPdfCanvas.width);
      const height = Math.min(sourcePdfCanvas.height, targetPdfCanvas.height);

      const sourceData = sourceCtx.getImageData(0, 0, width, height);
      const targetData = targetCtx.getImageData(0, 0, width, height);

      // Create a difference map
      const diffCanvas = document.createElement('canvas');
      diffCanvas.width = width;
      diffCanvas.height = height;
      const diffCtx = diffCanvas.getContext('2d');
      if (!diffCtx) {
        setIsComparing(false);
        return;
      }

      const diffData = diffCtx.createImageData(width, height);
      const threshold = 15; // Lower threshold for better sensitivity to text and image changes

      // Compare pixels and mark differences
      for (let i = 0; i < sourceData.data.length; i += 4) {
        const rDiff = Math.abs(sourceData.data[i] - targetData.data[i]);
        const gDiff = Math.abs(sourceData.data[i + 1] - targetData.data[i + 1]);
        const bDiff = Math.abs(sourceData.data[i + 2] - targetData.data[i + 2]);

        const totalDiff = rDiff + gDiff + bDiff;

        if (totalDiff > threshold) {
          diffData.data[i] = 255; // Red
          diffData.data[i + 1] = 0;
          diffData.data[i + 2] = 0;
          diffData.data[i + 3] = 255; // Alpha
        } else {
          diffData.data[i] = 0;
          diffData.data[i + 1] = 0;
          diffData.data[i + 2] = 0;
          diffData.data[i + 3] = 0;
        }
      }

      // Find bounding boxes for difference regions
      const regions = findDifferenceRegions(diffData, width, height);
      setDifferenceRegions(regions);

      // Draw difference overlays on both canvases
      drawDifferenceOverlay(sourcePdfCanvas, regions);
      drawDifferenceOverlay(targetPdfCanvas, regions);

      setIsComparing(false);
    }, 500);
  };

  if (!sourcePdfBase64 || !targetPdfBase64) {
    return null;
  }

  return (
    <div className="side-by-side-container">
      <div className="pdf-side">
        <div className="pdf-side-header">
          <select
            className="version-select"
            value={sourceVersion}
            onChange={(e) => onSourceVersionChange(e.target.value)}
          >
            {availableVersions.map((v) => (
              <option key={v} value={v}>VERSION {v}</option>
            ))}
          </select>
          <KiteTag icon="ki-checkmark-circle-f" className="production-badge">
            In Production
          </KiteTag>
          {differenceRegions.length > 0 && (
            <KiteTag variant="outline" className="differences-count">
              {`${differenceRegions.length} difference${
                differenceRegions.length !== 1 ? 's' : ''
              } found`}
            </KiteTag>
          )}
        </div>
        <div className="pdf-comparison-wrapper" ref={sourceCanvasRef}>
          <Document
            file={sourcePdfBase64}
            onLoadSuccess={(data) => {
              onSourceLoadSuccess(data);
              comparePdfPages();
            }}
            loading="Loading PDF..."
            error="Failed to load PDF."
            className="pdf-document"
          >
            <Page
              pageNumber={sourcePageNumber}
              scale={scale}
              className="pdf-page"
              renderTextLayer={true}
              renderAnnotationLayer={true}
              onRenderSuccess={comparePdfPages}
            />
          </Document>
          {isComparing && <div className="comparing-overlay">Comparing...</div>}
        </div>
      </div>
      <div className="pdf-side">
        <div className="pdf-side-header">
          <select
            className="version-select"
            value={targetVersion}
            onChange={(e) => onTargetVersionChange(e.target.value)}
          >
            {availableVersions.map((v) => (
              <option key={v} value={v}>VERSION {v}</option>
            ))}
          </select>
          <KiteTag className="obsolete-badge">Obsolete</KiteTag>
          {differenceRegions.length > 0 && (
            <KiteTag variant="outline" className="differences-count">
              {`${differenceRegions.length} difference${
                differenceRegions.length !== 1 ? 's' : ''
              } found`}
            </KiteTag>
          )}
        </div>
        <div className="pdf-comparison-wrapper" ref={targetCanvasRef}>
          <Document
            file={targetPdfBase64}
            onLoadSuccess={onTargetLoadSuccess}
            loading="Loading PDF..."
            error="Failed to load PDF."
            className="pdf-document"
          >
            <Page
              pageNumber={targetPageNumber}
              scale={scale}
              className="pdf-page"
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>
        </div>
      </div>
    </div>
  );
};

export default SideBySideComparison;
